import React, { useState } from 'react';
import { Modal, Form, Input, message, Typography } from 'antd';
import { LockOutlined, SafetyOutlined } from '@ant-design/icons';
import service, { scheduleTokenRefresh } from 'auth/FetchInterceptor';
import { usePermissions } from 'contexts/PermissionContext';
import { AUTH_TOKEN, REFRESH_TOKEN } from 'constants/AuthConstant';
import { useDispatch } from 'react-redux';
import { authenticated } from 'store/slices/authSlice';

const { Text, Title } = Typography;

const ChangePasswordModal = ({ visible, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { refreshUser } = usePermissions();
  const dispatch = useDispatch();

  const handleSubmit = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await service.post('/auth/change-password', {
        newPassword: values.newPassword,
      });
      
      // CRITICAL: Update tokens in localStorage with new tokens from response
      if (response.token) {
        localStorage.setItem(AUTH_TOKEN, response.token);
        dispatch(authenticated({ token: response.token }));
        scheduleTokenRefresh(response.token);
      }
      if (response.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN, response.refreshToken);
      }
      
      message.success('Password changed successfully!');
      form.resetFields();
      await refreshUser();
      onSuccess?.();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={null}
      open={visible}
      footer={null}
      closable={false}
      maskClosable={false}
      width={420}
      centered
    >
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ 
          width: 64, 
          height: 64, 
          borderRadius: '50%', 
          background: '#fff7e6', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          margin: '0 auto 16px'
        }}>
          <SafetyOutlined style={{ fontSize: 32, color: '#fa8c16' }} />
        </div>
        <Title level={4} style={{ marginBottom: 8 }}>Set Your New Password</Title>
        <Text type="secondary">
          You're using a temporary password. Please set a new password to continue.
        </Text>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="newPassword"
          label="New Password"
          rules={[
            { required: true, message: 'Enter new password' },
            { min: 6, message: 'Password must be at least 6 characters' },
          ]}
        >
          <Input.Password 
            prefix={<LockOutlined />} 
            placeholder="Enter new password"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password 
            prefix={<LockOutlined />} 
            placeholder="Confirm new password"
            size="large"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: 16,
              fontWeight: 600,
              background: '#1890ff',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Updating...' : 'Set New Password'}
          </button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
